/**
 * IndexedDB wrapper using idb library
 * @module db
 */

import { openDB } from 'idb';
import { generateClientId, formatDate } from './utils.js';

/** Database name */
const DB_NAME = 'expense-tracker-db';

/** Database version */
const DB_VERSION = 1;

/** localStorage key for legacy offline queue */
const LEGACY_QUEUE_KEY = 'expense_tracker_offline_queue';

/** localStorage key for migrated backup */
const LEGACY_QUEUE_MIGRATED_KEY = 'expense_tracker_offline_queue_migrated';

/**
 * @typedef {Object} ExpenseRecord
 * @property {string} clientId - UUID, generated client-side
 * @property {string|null} serverId - Row identifier from Sheets (if synced)
 * @property {string} date - YYYY-MM-DD
 * @property {number} amount
 * @property {string} currency
 * @property {number} rate
 * @property {number} amountRM
 * @property {string} category
 * @property {string} subcategory
 * @property {string} payment
 * @property {string} note
 * @property {string} type
 * @property {string} trip
 * @property {boolean} synced
 * @property {number} created - Timestamp
 * @property {number} updated - Timestamp
 * @property {number} version
 */

/**
 * @typedef {'pending' | 'processing' | 'failed'} SyncStatus
 */

/**
 * @typedef {Object} SyncQueueItem
 * @property {number} id - Auto-incremented
 * @property {'create' | 'update' | 'delete'} type
 * @property {string} clientId
 * @property {Partial<ExpenseRecord>} data
 * @property {number} timestamp
 * @property {SyncStatus} status
 * @property {number} retryCount
 * @property {string|null} lastError
 * @property {number|null} lastAttempt
 */

/**
 * @typedef {Object} GetExpensesOptions
 * @property {string} [startDate] - Filter by date >= startDate
 * @property {string} [endDate] - Filter by date <= endDate
 * @property {boolean} [syncedOnly] - Only return synced expenses
 * @property {boolean} [unsyncedOnly] - Only return unsynced expenses
 * @property {number} [limit] - Maximum number of results
 */

/**
 * Initialize and return the database connection
 * @returns {Promise<import('idb').IDBPDatabase>}
 */
export async function initDB() {
  const db = await openDB(DB_NAME, DB_VERSION, {
    upgrade(db, oldVersion, newVersion, transaction) {
      // Expenses store
      if (!db.objectStoreNames.contains('expenses')) {
        const expenseStore = db.createObjectStore('expenses', { keyPath: 'clientId' });
        expenseStore.createIndex('date', 'date');
        expenseStore.createIndex('synced', 'synced');
        expenseStore.createIndex('serverId', 'serverId');
      }

      // Sync queue store
      if (!db.objectStoreNames.contains('syncQueue')) {
        const syncStore = db.createObjectStore('syncQueue', {
          keyPath: 'id',
          autoIncrement: true
        });
        syncStore.createIndex('timestamp', 'timestamp');
        syncStore.createIndex('status', 'status');
      }

      // Config store
      if (!db.objectStoreNames.contains('config')) {
        db.createObjectStore('config', { keyPath: 'key' });
      }

      // Rates store
      if (!db.objectStoreNames.contains('rates')) {
        db.createObjectStore('rates', { keyPath: 'date' });
      }

      // Meta store
      if (!db.objectStoreNames.contains('meta')) {
        db.createObjectStore('meta', { keyPath: 'key' });
      }
    }
  });

  return db;
}

/**
 * Add a new expense and enqueue for sync
 * @param {import('idb').IDBPDatabase} db
 * @param {Partial<ExpenseRecord>} expense - Expense data (clientId optional)
 * @returns {Promise<ExpenseRecord>} Created expense record
 */
export async function addExpense(db, expense) {
  const now = Date.now();
  const clientId = expense.clientId || generateClientId();

  /** @type {ExpenseRecord} */
  const record = {
    clientId,
    serverId: null,
    date: expense.date || formatDate(new Date()),
    amount: expense.amount || 0,
    currency: expense.currency || 'MYR',
    rate: expense.rate || 1,
    amountRM: expense.amountRM || expense.amount || 0,
    category: expense.category || '',
    subcategory: expense.subcategory || '',
    payment: expense.payment || '',
    note: expense.note || '',
    type: expense.type || '',
    trip: expense.trip || '',
    synced: false,
    created: now,
    updated: now,
    version: 1
  };

  /** @type {Omit<SyncQueueItem, 'id'>} */
  const queueItem = {
    type: 'create',
    clientId,
    data: record,
    timestamp: now,
    status: 'pending',
    retryCount: 0,
    lastError: null,
    lastAttempt: null
  };

  // Transaction across both stores
  const tx = db.transaction(['expenses', 'syncQueue'], 'readwrite');
  await Promise.all([
    tx.objectStore('expenses').put(record),
    tx.objectStore('syncQueue').add(queueItem),
    tx.done
  ]);

  return record;
}

/**
 * Query expenses with optional filters
 * @param {import('idb').IDBPDatabase} db
 * @param {GetExpensesOptions} [options={}]
 * @returns {Promise<ExpenseRecord[]>}
 */
export async function getExpenses(db, options = {}) {
  const { startDate, endDate, syncedOnly, unsyncedOnly, limit } = options;

  let results;

  // Use index-based query when filtering by synced status
  if (syncedOnly === true) {
    results = await db.getAllFromIndex('expenses', 'synced', true);
  } else if (unsyncedOnly === true) {
    results = await db.getAllFromIndex('expenses', 'synced', false);
  } else {
    results = await db.getAll('expenses');
  }

  // Apply date filters
  if (startDate || endDate) {
    results = results.filter(expense => {
      if (startDate && expense.date < startDate) return false;
      if (endDate && expense.date > endDate) return false;
      return true;
    });
  }

  // Sort by date descending, then by created descending
  results.sort((a, b) => {
    const dateCompare = b.date.localeCompare(a.date);
    if (dateCompare !== 0) return dateCompare;
    return b.created - a.created;
  });

  // Apply limit
  if (limit && limit > 0) {
    results = results.slice(0, limit);
  }

  return results;
}

/**
 * Get all pending sync queue items
 * @param {import('idb').IDBPDatabase} db
 * @returns {Promise<SyncQueueItem[]>}
 */
export async function getPendingSyncItems(db) {
  const allItems = await db.getAll('syncQueue');
  // Return pending and failed items, sorted by timestamp
  return allItems
    .filter(item => item.status === 'pending' || item.status === 'failed')
    .sort((a, b) => a.timestamp - b.timestamp);
}

/**
 * Update the status of a sync queue item
 * @param {import('idb').IDBPDatabase} db
 * @param {number} id - Queue item ID
 * @param {SyncStatus} status - New status
 * @param {number} [retryCount] - Updated retry count
 * @param {string|null} [error] - Error message if failed
 * @returns {Promise<void>}
 */
export async function updateSyncStatus(db, id, status, retryCount, error = null) {
  const item = await db.get('syncQueue', id);
  if (!item) return;

  item.status = status;
  item.lastAttempt = Date.now();

  if (retryCount !== undefined) {
    item.retryCount = retryCount;
  }

  if (error !== null) {
    item.lastError = error;
  }

  await db.put('syncQueue', item);
}

/**
 * Mark an expense as synced and remove from queue
 * @param {import('idb').IDBPDatabase} db
 * @param {string} clientId - Client-side expense ID
 * @param {string} serverId - Server-assigned ID
 * @returns {Promise<void>}
 */
export async function markSynced(db, clientId, serverId) {
  const tx = db.transaction(['expenses', 'syncQueue'], 'readwrite');

  // Update expense record
  const expense = await tx.objectStore('expenses').get(clientId);
  if (expense) {
    expense.synced = true;
    expense.serverId = serverId;
    expense.updated = Date.now();
    await tx.objectStore('expenses').put(expense);
  }

  // Remove matching queue items
  const queueStore = tx.objectStore('syncQueue');
  const allItems = await queueStore.getAll();
  for (const item of allItems) {
    if (item.clientId === clientId) {
      await queueStore.delete(item.id);
    }
  }

  await tx.done;
}

/**
 * Remove a sync queue item by ID
 * @param {import('idb').IDBPDatabase} db
 * @param {number} id - Queue item ID
 * @returns {Promise<void>}
 */
export async function removeSyncItem(db, id) {
  await db.delete('syncQueue', id);
}

/**
 * Get count of pending sync items
 * @param {import('idb').IDBPDatabase} db
 * @returns {Promise<number>}
 */
export async function getPendingCount(db) {
  const items = await getPendingSyncItems(db);
  return items.length;
}

/**
 * Recover orphaned items stuck in 'processing' status
 * Resets them to 'pending' for retry
 * @param {import('idb').IDBPDatabase} db
 * @returns {Promise<number>} Number of items recovered
 */
export async function recoverOrphanedItems(db) {
  const tx = db.transaction('syncQueue', 'readwrite');
  const store = tx.objectStore('syncQueue');
  const allItems = await store.getAll();

  let recoveredCount = 0;
  const staleThreshold = Date.now() - 60 * 1000; // 1 minute (was 5 minutes)

  for (const item of allItems) {
    if (item.status === 'processing') {
      // Reset if no recent attempt or stuck for too long
      if (!item.lastAttempt || item.lastAttempt < staleThreshold) {
        item.status = 'pending';
        item.lastError = 'Recovered from stuck processing state';
        await store.put(item);
        recoveredCount++;
      }
    }
  }

  await tx.done;
  return recoveredCount;
}

/**
 * Migrate legacy localStorage queue to IndexedDB
 * @param {import('idb').IDBPDatabase} db
 * @returns {Promise<number>} Number of items migrated
 */
export async function migrateFromLocalStorage(db) {
  if (typeof localStorage === 'undefined') return 0;

  // Idempotency guard: check if migration already completed
  const migrationDone = await getMeta(db, 'localStorageMigrated');
  if (migrationDone) return 0;

  const legacyData = localStorage.getItem(LEGACY_QUEUE_KEY);
  if (!legacyData) return 0;

  let items;
  try {
    items = JSON.parse(legacyData);
  } catch (e) {
    console.error('[DB] Failed to parse legacy queue:', e);
    return 0;
  }

  if (!Array.isArray(items) || items.length === 0) return 0;

  const now = Date.now();
  const tx = db.transaction(['expenses', 'syncQueue'], 'readwrite');
  const expenseStore = tx.objectStore('expenses');
  const queueStore = tx.objectStore('syncQueue');

  let migratedCount = 0;

  for (const legacyItem of items) {
    try {
      const clientId = generateClientId();

      /** @type {ExpenseRecord} */
      const expense = {
        clientId,
        serverId: null,
        date: legacyItem.date || formatDate(new Date()),
        amount: parseFloat(legacyItem.amount) || 0,
        currency: legacyItem.currency || 'MYR',
        rate: parseFloat(legacyItem.rate) || 1,
        amountRM: parseFloat(legacyItem.amountRM) || parseFloat(legacyItem.amount) || 0,
        category: legacyItem.category || '',
        subcategory: legacyItem.subcategory || '',
        payment: legacyItem.payment || '',
        note: legacyItem.note || '',
        type: legacyItem.type || '',
        trip: legacyItem.trip || '',
        synced: false,
        created: legacyItem.timestamp || now,
        updated: now,
        version: 1
      };

      /** @type {Omit<SyncQueueItem, 'id'>} */
      const queueItem = {
        type: 'create',
        clientId,
        data: expense,
        timestamp: legacyItem.timestamp || now,
        status: 'pending',
        retryCount: 0,
        lastError: null,
        lastAttempt: null
      };

      await expenseStore.put(expense);
      await queueStore.add(queueItem);
      migratedCount++;
    } catch (e) {
      console.error('[DB] Failed to migrate item:', e, legacyItem);
    }
  }

  await tx.done;

  // Preserve original data as backup, then remove
  if (migratedCount > 0) {
    localStorage.setItem(LEGACY_QUEUE_MIGRATED_KEY, legacyData);
    localStorage.removeItem(LEGACY_QUEUE_KEY);
    await setMeta(db, 'localStorageMigrated', true);
    console.log(`[DB] Migrated ${migratedCount} items from localStorage`);
  }

  return migratedCount;
}

/**
 * Cache configuration data
 * @param {import('idb').IDBPDatabase} db
 * @param {Object} config - Configuration object
 * @returns {Promise<void>}
 */
export async function cacheConfig(db, config) {
  await db.put('config', {
    key: 'appConfig',
    data: config,
    cached: Date.now()
  });
}

/**
 * Retrieve cached configuration
 * @param {import('idb').IDBPDatabase} db
 * @returns {Promise<Object|null>} Configuration object or null
 */
export async function getCachedConfig(db) {
  const record = await db.get('config', 'appConfig');
  return record ? record.data : null;
}

/**
 * Cache exchange rates for a specific date
 * @param {import('idb').IDBPDatabase} db
 * @param {string} date - Date key (YYYY-MM-DD)
 * @param {Object} rates - Exchange rates object
 * @returns {Promise<void>}
 */
export async function cacheRates(db, date, rates) {
  await db.put('rates', {
    date,
    data: rates,
    cached: Date.now()
  });
}

/**
 * Retrieve cached exchange rates for a date
 * @param {import('idb').IDBPDatabase} db
 * @param {string} date - Date key (YYYY-MM-DD)
 * @returns {Promise<Object|null>} Rates object or null
 */
export async function getCachedRates(db, date) {
  const record = await db.get('rates', date);
  return record ? record.data : null;
}

/**
 * Store metadata value
 * @param {import('idb').IDBPDatabase} db
 * @param {string} key - Metadata key
 * @param {*} value - Value to store
 * @returns {Promise<void>}
 */
export async function setMeta(db, key, value) {
  await db.put('meta', { key, value, updated: Date.now() });
}

/**
 * Retrieve metadata value
 * @param {import('idb').IDBPDatabase} db
 * @param {string} key - Metadata key
 * @returns {Promise<*>} Stored value or undefined
 */
export async function getMeta(db, key) {
  const record = await db.get('meta', key);
  return record ? record.value : undefined;
}
